import React, { useState } from 'react';
import { Button, Typography, Alert } from '@mui/material';
import SliderWithInput from '../../ChartOptions/SliderWithInput';
import { updateStateBasedProp } from './ButtonContent.utils';
import { SLIDER_CONFIGS, MAX_FILE_SIZE } from './ButtonContent.constants';
import AlignmentControls from './AlignmentControls';
import { ImageProps, StateBasedProps } from './ButtonContent';

interface ImageControlsProps {
  imageUrl: string;
  imageName: string;
  currentImageProps: ImageProps;
  imageProps: ImageProps | StateBasedProps<ImageProps>;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  updateImage: (updates: any) => void;
  activeState: 'default' | 'hover';
  isMultiContent?: boolean;
  iconColor?: string;
}

const ImageControls: React.FC<ImageControlsProps> = ({
  imageUrl,
  imageName,
  currentImageProps,
  imageProps,
  handleFileUpload,
  updateImage,
  activeState,
  isMultiContent,
  iconColor,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const updateImageProps = (updates: Partial<ImageProps>) => {
    const newImageProps = updateStateBasedProp(imageProps, updates, activeState);
    updateImage({ imageProps: newImageProps });
  };

  const handleFileUploadWithValidation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(null);

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds 250KB limit`);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    // File is valid, proceed with upload
    setSuccess(`Image uploaded`);
    handleFileUpload(event);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <Button
        variant="outlined"
        component="label"
        fullWidth
        sx={{
          py: 1,
          borderStyle: 'solid',
          borderColor: '#666666',
          color: '#333333',
          border: '1 px',
          backgroundColor: '#e0e0e0',
          '&:hover': {
            backgroundColor: '#e0e0e0',
            borderColor: '#555555',
          },
          '&:active': {
            backgroundColor: '#d0d0d0',
          },
          fontWeight: 600,
          textTransform: 'none',
        }}
      >
        {imageUrl ? 'Change Image' : 'Upload Image'}
        <input type="file" hidden accept="image/*" onChange={handleFileUploadWithValidation} />
      </Button>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mt: 1 }}>
          {success}
        </Alert>
      )}

      {imageName && !error && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Selected: {imageName}
        </Typography>
      )}

      <Typography variant="caption" color={iconColor}>
        Maximum upload size: 250KB
      </Typography>

      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Width
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={
            currentImageProps.width
              ? Number(currentImageProps.width)
              : SLIDER_CONFIGS.imageWidth.default
          }
          sliderMinMax={SLIDER_CONFIGS.imageWidth}
          changeValue={(value: number) => updateImageProps({ width: value })}
        />
      </div>

      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Height
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={
            currentImageProps.height
              ? Number(currentImageProps.height)
              : SLIDER_CONFIGS.imageHeight.default
          }
          sliderMinMax={SLIDER_CONFIGS.imageHeight}
          changeValue={(value: number) => updateImageProps({ height: value })}
        />
      </div>

      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Border Radius
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentImageProps.borderRadius ?? SLIDER_CONFIGS.borderRadius.default}
          sliderMinMax={SLIDER_CONFIGS.borderRadius}
          changeValue={(value: number) => updateImageProps({ borderRadius: value })}
        />
      </div>

      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Image Transparency
        </div>
        <SliderWithInput
          percent={true}
          sliderValue={currentImageProps.opacity ?? SLIDER_CONFIGS.opacity.default}
          sliderMinMax={SLIDER_CONFIGS.opacity}
          changeValue={(value: number) => updateImageProps({ opacity: value })}
        />
      </div>

      <AlignmentControls
        type="image"
        iconColor={iconColor}
        currentProps={currentImageProps}
        updateProps={updateImageProps}
        isMultiContent={isMultiContent}
      />
    </div>
  );
};

export default ImageControls;
